/**
 * 联网搜索件的声明式元数据（T12 / T18 收口）。
 *
 * 约定与 T06 一致：坐标是 390x844 画布上的**绝对逻辑坐标**（含顶部 24dp 状态栏偏移），
 * 同时作为「运行期 `useComponentMeta()` 登记」与「静态移植清单」的单一事实来源。
 *
 * 本文件只声明**固定 id** 的可见部件：
 * - `chat.search.toggleButton`：对话页右上角联网搜索开关（`SearchToggle.tsx` 登记）；
 * - `chat.search.notice`：空结果 / 检索失败的非阻断提示（`SearchNotice.tsx` 登记）。
 *
 * 引用来源（`CitationSources.tsx`）挂在助手气泡之下，id 由宿主气泡 id 派生：
 * `chat.message.assistant.<消息id>.citations` / `.citations.toggle` / `.citations.item.<index>`，
 * 坐标也按宿主气泡布局换算 —— 这类**逐消息实例**没有固定 id，与 `chat.message.assistant` /
 * `chat.message.ttsButton` / `community.card` / `providers.item` 属于同一种
 * 「静态模板 + 运行时实例」约定，运行期登记即可，不在此处再写一份死声明。
 *
 * T12 审核挂账的 id 不一致问题（T18 修复）：本文件原先还声明了
 * `chat.search.citationList` / `chat.search.citationToggle` / `chat.search.citationItem`
 * 三条固定 id，但它们**从未被运行期登记**（`CitationSources.tsx` 登记的是上面那套派生 id），
 * 属于死声明。T18 选择「删除未用声明」并把运行期 id 约定写进本注释，
 * 保证「本文件声明的 id ≡ 运行期登记的 id」这一约束成立。
 */

import type { ComponentMeta } from '../../core/types';
import { buildComponentMeta } from '../../ui';
import type { ComponentMetaInput } from '../../ui';

/** 本组部件所属页面（复用对话页 page 标识，便于清单聚合）。 */
export const SEARCH_PAGE = 'chat';

export const SEARCH_COMPONENTS: ComponentMetaInput[] = [
  {
    id: 'chat.search.toggleButton',
    page: SEARCH_PAGE,
    role: 'button',
    label: '联网搜索开关',
    layout: { x: 338, y: 30, width: 44, height: 44 },
    zIndex: 6,
    props: ['enabled'],
    notes:
      '对话页右上角联网搜索开关（触摸目标 44x44，已与 chat.header.title 留出间距）；开启后用户输入先经核心搜索层检索再做引用注入；安卓用 MaterialButton（iconToggle，选中态用 tonal 底色）',
  },
  {
    id: 'chat.search.notice',
    page: SEARCH_PAGE,
    role: 'text',
    label: '搜索状态提示',
    layout: { x: 16, y: 700, width: 358, height: 20 },
    zIndex: 9,
    notes:
      '空结果 / 搜索失败时的非阻断提示，不打断对话；与 chat.statusHint 共用同一槽位（两者互斥：仅在未取消生成时出现）；安卓用 TextView（单行，次要文本色）',
  },
];

const COMPONENT_INDEX = new Map<string, ComponentMetaInput>(
  SEARCH_COMPONENTS.map((meta) => [meta.id, meta]),
);

/** 按 id 取声明式元数据输入；未登记直接抛错，避免清单静默缺项。 */
export function searchMeta(id: string): ComponentMetaInput {
  const found = COMPONENT_INDEX.get(id);
  if (found === undefined) throw new Error(`[search] 未登记的部件：${id}`);
  return found;
}

/** 按 id 取声明布局（页面内联定位与清单坐标共用一份数据）。 */
export function searchLayout(id: string): ComponentMetaInput['layout'] {
  return searchMeta(id).layout;
}

/** 按 id 构造完整 `ComponentMeta`，供 `useComponentMeta()` 登记。 */
export function searchComponentMeta(id: string): ComponentMeta {
  return buildComponentMeta(searchMeta(id));
}
